import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import useNow from '../hooks/useNow';
import { formatCountdown, parseServerTime } from '../utils/missionMath';
import { LIVES_ICON } from '../utils/lairAssets';

/** Time left until the next life (ms), null when lives are full / unknown. */
export function nextLifeLeftMs(lives, serverNow) {
  const at = parseServerTime(lives?.nextLifeAt);
  return Number.isFinite(at) ? Math.max(0, at - serverNow) : null;
}

/**
 * Lives of mini-games (v1.51.0) in the resources row of the Lair header: ❤ count + timer of the next life
 * while below the max. When the timer runs out — `onRefresh` (the server restores the life lazily).
 */
export default function LivesChip({ lives, offsetMs = 0, onRefresh }) {
  const { t } = useTranslation();
  const hasTimer = !!lives?.nextLifeAt;
  const now = useNow(1000, hasTimer);
  const left = nextLifeLeftMs(lives, now + (Number(offsetMs) || 0));
  const refreshedFor = useRef(null);

  useEffect(() => {
    if (left === 0 && onRefresh && refreshedFor.current !== lives?.nextLifeAt) {
      refreshedFor.current = lives?.nextLifeAt;
      onRefresh();
    }
  }, [left, lives?.nextLifeAt, onRefresh]);

  if (!lives) return null;
  const count = Number(lives.lives) || 0;
  return (
    <div
      className={`lair-res lair-res--lives${count === 0 ? ' lair-res--empty' : ''}`}
      title={t('lives.title', 'Lives: a lost game costs one')}
      data-testid="lives-chip"
    >
      <img src={LIVES_ICON} alt={t('lives.name', 'Lives')} />
      <span>{count}</span>
      {left !== null && <span className="lair-res__timer">{formatCountdown(left)}</span>}
    </div>
  );
}
